import { createReducer, on } from '@ngrx/store'
import { Barter, BarterResponseDto, ParcelBarterEntity,  BarterState, MonthYearUnit } from '../../../api/backend'
import { format } from 'date-fns'
import { BarterActions } from './action-types'
import cloneDeep from 'lodash/cloneDeep'
import { BarterTabEnum } from '../enums/barter-tab.enum'

const lastDayOfYear = format(new Date(new Date().getFullYear(), 11, 31), 'y-MM-dd')

const INIT_STATE: IBarterState = {
    barter: {
        barterNumberOfficial: null,
        concernedId: null,
        concernedSubject: null,
        counterpartyId: null,
        counterparty: null,
        durationMonths: 0,
        durationMonthsUnit: MonthYearUnit.Y,
        efficiencyFromDate: null,
        endDate: null,
        endType: null,
        expirationDate: null,
        id: null,
        incomingAmount: 0,
        incomingPayoutDate: lastDayOfYear,
        indefinite: true,
        note: null,
        noticePeriodMonths: 0,
        noticePeriodMonthsUnit: MonthYearUnit.Y,
        outgoingAmount: 0,
        outgoingPayoutDate: lastDayOfYear,
        paymentsFromEconomicYear: true,
        resignDate: null,
        reusedPreviousExpiration: false,
        state: BarterState.PROPOSAL,
        toEconomicYear: true,
        previousVersionId: null,
        appendixNumber: null,
        versionType: null
    },
    concernedParcelIds: [],
    concernedParcels: [],
    counterpartyParcels: [],
    counterpartyParcelIds: [],
    concernedParcelAcreages: [],
    concernedParcelAcreagesSum: 0,
    counterpartyParcelAcreages: [],
    counterpartyParcelAcreagesSum: 0,
    concernedParcelGroupsOwnerships: [], // map of key 'katuzeKod-lv' -> ownership object
    counterpartyParcelGroupsOwnerships: [],// map of key 'katuzeKod-lv' -> ownership object
    passedReceivedParcelsMapMode: null, // null || 'pass' || 'receive'
    passedParcelIds: [],
    passedParcels: [],
    passedParcelAcreages: [],
    passedParcelAcreagesSum: 0,
    passedParcelGeometries: [],
    passedParcelGroupsOwnerships: [], // map of key 'katuzeKod-lv' -> ownership object
    receivedParcelIds: [],
    receivedParcels: [],
    receivedParcelAcreages: [],
    receivedParcelAcreagesSum: 0,
    receivedParcelGeometries: [],
    receivedParcelGroupsOwnerships: [],// map of key 'katuzeKod-lv' -> ownership object
    parcelConflicts: [],
    barterResult: {},
    isEditingBarter: false,
    isAnyConcernedOrCounterpartyParcelUpdated: false,
    parcelOrder: [{
        sortedBy: 'parcis',
        desc: false
    }, {
        sortedBy: 'katuzeLv',
        desc: false
    }],
    incomingPayments: [],
    outgoingPayments: [],
    selectedTab: BarterTabEnum.BasicInfo,
    reloadMapParcels: true,
}

export const barterReducer = createReducer(
    INIT_STATE,
    on(BarterActions.setBarter, (state, { barter }): IBarterState => {
        const newState = cloneDeep(state)
        newState.barter = barter
        return newState
    }),
    // ==> barter-create
    on(BarterActions.setBarterIsEditing, (state, {value}): IBarterState => {
        const newState = cloneDeep(state)
        newState.isEditingBarter = value
        return newState
    }),
    on(BarterActions.setBarterConcerned, (state, {concernedSubject}): IBarterState => {
        const newState = cloneDeep(state)
        newState.barter.concernedSubject = concernedSubject
        newState.barter.concernedId = concernedSubject.id
        return newState
    }),
    on(BarterActions.setBarterCounterparty, (state, {counterparty}): IBarterState => {
        const newState = cloneDeep(state)
        newState.barter.counterparty = counterparty
        newState.barter.counterpartyId = counterparty.id
        return newState
    }),
    on(BarterActions.setBarterAppendixNumber, (state, {appendixNumber}): IBarterState => {
        const newState = cloneDeep(state)
        newState.barter.appendixNumber = appendixNumber
        return newState
    }),
    on(BarterActions.setBarterFurtherRentPossible, (state, {value}): IBarterState => {
        const newState = cloneDeep(state)
        newState.barter.furtherRentPossible = value
        return newState
    }),
    on(BarterActions.setBarterIncomingAmount, (state, {amount}): IBarterState => {
        const newState = cloneDeep(state)
        newState.barter.incomingAmount = amount
        return newState
    }),
    on(BarterActions.setBarterOutgoingAmount, (state, {amount}): IBarterState => {
        const newState = cloneDeep(state)
        newState.barter.outgoingAmount = amount
        return newState
    }),
    on(BarterActions.setBarterIncomingPayoutDate, (state, {date}): IBarterState => {
        const newState = cloneDeep(state)
        newState.barter.incomingPayoutDate = date
        return newState
    }),
    on(BarterActions.setBarterOutgoingPayoutDate, (state, {date}): IBarterState => {
        const newState = cloneDeep(state)
        newState.barter.outgoingPayoutDate = date
        return newState
    }),
    on(BarterActions.resetBarterIsAnyConcernedOrCounterpartyParcelUpdated, (state): IBarterState => {
        const newState = cloneDeep(state)
        newState.isAnyConcernedOrCounterpartyParcelUpdated = INIT_STATE.isAnyConcernedOrCounterpartyParcelUpdated
        return newState
    }),
    on(BarterActions.resetBarterCounterparty, (state): IBarterState => {
        const newState = cloneDeep(state)
        newState.barter.counterparty = INIT_STATE.barter.counterparty
        newState.barter.counterpartyId = INIT_STATE.barter.counterpartyId
        return newState
    }),
    // !==
    on(BarterActions.setBarterSelectedTab, (state, { tab }): IBarterState => {
        const newState = cloneDeep(state)
        newState.selectedTab = tab
        return newState
    }),
    on(BarterActions.setBarterExpirationDate, (state, { date }): IBarterState => {
        const newState = cloneDeep(state)
        newState.barter.expirationDate = date ? date : INIT_STATE.barter.expirationDate
        return newState
    }),
    on(BarterActions.setBarterResult, (state, { result }): IBarterState => {
        const newState = cloneDeep(state)
        newState.barterResult = result
        return newState
    }),
    on(BarterActions.setIncomingPayments, (state, { payments }): IBarterState => {
        const newState = cloneDeep(state)
        newState.incomingPayments = payments
        return newState
    }),
    on(BarterActions.setOutgoingPayments, (state, { payments }): IBarterState => {
        const newState = cloneDeep(state)
        newState.outgoingPayments = payments
        return newState
    }),
    on(BarterActions.setParcelConflicts, (state, { parcelConflicts }): IBarterState => {
        const newState = cloneDeep(state)
        newState.parcelConflicts = parcelConflicts
        return newState
    }),
    on(BarterActions.setConcernedParcelGroupsOwnerships, (state, { ownerships }): IBarterState => {
        const newState = cloneDeep(state)
        newState.concernedParcelGroupsOwnerships = ownerships
        return newState
    }),
    on(BarterActions.addConcernedParcelGroupsOwnerships, (state, { ownerships }): IBarterState => {
        const newState = cloneDeep(state)
        newState.concernedParcelGroupsOwnerships = ownerships ? [...newState.concernedParcelGroupsOwnerships || [], ...ownerships] : newState.concernedParcelGroupsOwnerships
        return newState
    }),
    on(BarterActions.setCounterpartyParcelGroupsOwnerships, (state, { ownerships }): IBarterState => {
        const newState = cloneDeep(state)
        newState.counterpartyParcelGroupsOwnerships = ownerships
        return newState
    }),
    on(BarterActions.addCounterpartyParcelGroupsOwnerships, (state, { ownerships }): IBarterState => {
        const newState = cloneDeep(state)
        newState.counterpartyParcelGroupsOwnerships = ownerships ? [...newState.counterpartyParcelGroupsOwnerships || [], ...ownerships] : newState.counterpartyParcelGroupsOwnerships
        return newState
    }),
    on(BarterActions.setPassedParcelGroupsOwnerships, (state, { ownerships }): IBarterState => {
        const newState = cloneDeep(state)
        newState.passedParcelGroupsOwnerships = ownerships
        return newState
    }),
    on(BarterActions.setReceivedParcelGroupsOwnerships, (state, { ownerships }): IBarterState => {
        const newState = cloneDeep(state)
        newState.receivedParcelGroupsOwnerships = ownerships
        return newState
    }),
    on(BarterActions.setPassedReceivedParcelsMapMode, (state, { mode }): IBarterState => {
        const newState = cloneDeep(state)
        newState.passedReceivedParcelsMapMode = mode
        return newState
    }),
    on(BarterActions.setBarterPaymentsFromEconomicYear, (state, { economicYear }): IBarterState => {
        const newState = cloneDeep(state)
        newState.barter.paymentsFromEconomicYear = economicYear
        return newState
    }),
    on(BarterActions.setBarterVersionType, (state, { versionType }): IBarterState => {
        const newState = cloneDeep(state)
        newState.barter.versionType = versionType
        return newState
    }),
    on(BarterActions.setBarterToEconomicYear, (state, { toEconomicYear }): IBarterState => {
        const newState = cloneDeep(state)
        newState.barter.toEconomicYear = toEconomicYear
        return newState
    }),
    on(BarterActions.setBarterNoticePeriod, (state, { noticePeriodMonths, noticePeriodMonthsUnit }): IBarterState => {
        const newState = cloneDeep(state)
        newState.barter.noticePeriodMonths = noticePeriodMonths
        newState.barter.noticePeriodMonthsUnit = noticePeriodMonthsUnit
        return newState
    }),
    on(BarterActions.setBarterConcernedParcels, (state, { concernedParcels, concernedParcelIds }): IBarterState => {
        const newState = cloneDeep(state)
        newState.concernedParcels = concernedParcels
        newState.concernedParcelIds = concernedParcelIds
        return newState
    }),
    on(BarterActions.setBarterConcernedParcelAcreages, (state, { acreages }): IBarterState => {
        const newState = cloneDeep(state)
        newState.concernedParcelAcreages = acreages
        return newState
    }),
    on(BarterActions.setBarterConcernedParcelAcreageSum, (state, { sum }): IBarterState => {
        const newState = cloneDeep(state)
        newState.concernedParcelAcreagesSum = sum
        return newState
    }),
    on(BarterActions.setBarterIsAnyConcernedOrCounterpartyParcelUpdated, (state, { value }): IBarterState => {
        const newState = cloneDeep(state)
        newState.isAnyConcernedOrCounterpartyParcelUpdated = value
        return newState
    }),
    on(BarterActions.setBarterReloadMapParcels, (state, { reload }): IBarterState => {
        const newState = cloneDeep(state)
        newState.reloadMapParcels = reload
        return newState
    }),
    on(BarterActions.resetPassedReceivedParcelsMapMode, (state): IBarterState => {
        const newState = cloneDeep(state)
        newState.passedReceivedParcelsMapMode = INIT_STATE.passedReceivedParcelsMapMode
        return newState
    }),
    on(BarterActions.setBarterCounterpartyParcelErrorHidden, (state, { barterParcelGroups, barterParcelGroupIds }): IBarterState => {
        const newState = cloneDeep(state)
        newState.counterpartyParcels = barterParcelGroups
        newState.counterpartyParcelIds = barterParcelGroupIds
        return newState
    }),
    on(BarterActions.setBarterConcernedParcelErrorHidden, (state, { barterParcelGroups, barterParcelGroupIds }): IBarterState => {
        const newState = cloneDeep(state)
        newState.concernedParcels = barterParcelGroups
        newState.concernedParcelIds = barterParcelGroupIds
        return newState
    }),
    on(BarterActions.setBarterReceivedParcelErrorHidden, (state, { barterParcelGroups, barterParcelGroupIds }): IBarterState => {
        const newState = cloneDeep(state)
        newState.receivedParcels = barterParcelGroups
        newState.receivedParcelIds = barterParcelGroupIds
        return newState
    }),
    on(BarterActions.setBarterPassedParcelErrorHidden, (state, { barterParcelGroups, barterParcelGroupIds }): IBarterState => {
        const newState = cloneDeep(state)
        newState.passedParcels = barterParcelGroups
        newState.passedParcelIds = barterParcelGroupIds
        return newState
    }),
    /**
     * Set array of ordering settings to used to sort barcel parcels.
     * It is array of 2 objects with attributes sortedBy and desc.
     * If method parameter orderBy is different from first item of array sortedBy
     * then first item replace second item and the first item is built from
     * new orderBy.
     * If parameter orderBy is the same as the first item, then only the attribute
     * desc is reverted.
     */
    on(BarterActions.setParcelOrder, (state, { orderBy }): IBarterState => {
        const newState = cloneDeep(state)
        if (state.parcelOrder[0].sortedBy === orderBy) {
            newState.parcelOrder[0].desc = !newState.parcelOrder[0].desc
        } else {
            newState.parcelOrder[1].sortedBy = newState.parcelOrder[0].sortedBy
            newState.parcelOrder[1].desc = newState.parcelOrder[0].desc
            newState.parcelOrder[0].sortedBy = orderBy
            newState.parcelOrder[0].desc = false
        }
        return newState
    }),
    on(BarterActions.setBarterConcernedParcelsAndAcreages, (state, payload): IBarterState => {
        const newState = cloneDeep(state)
        newState.concernedParcels = payload.parcels
        newState.concernedParcelIds = payload.parcelIds
        newState.concernedParcelAcreages = payload.acreages
        newState.concernedParcelAcreagesSum = payload.sum
        return newState
    }),
    on(BarterActions.setCounterpartyParcels, (state, payload): IBarterState => {
        const newState = cloneDeep(state)
        newState.counterpartyParcels = payload.counterpartyParcels
        newState.counterpartyParcelIds = payload.counterpartyParcelIds
        return newState
    }),
    on(BarterActions.setBarterCounterpartyParcelAcreages, (state, payload): IBarterState => {
        const newState = cloneDeep(state)
        newState.counterpartyParcelAcreages = payload.acreages
        newState.counterpartyParcelAcreagesSum = payload.sum
        return newState
    }),
    on(BarterActions.setBarterCounterpartyParcelsAndAcreages, (state, payload): IBarterState => {
        const newState = cloneDeep(state)
        newState.counterpartyParcels = payload.parcels
        newState.counterpartyParcelIds = payload.parcelIds
        newState.counterpartyParcelAcreages = payload.acreages
        newState.counterpartyParcelAcreagesSum = payload.sum
        return newState
    }),
    on(BarterActions.setPassedParcels, (state, payload): IBarterState => {
        const newState = cloneDeep(state)
        newState.passedParcels = payload.passedParcels
        newState.passedParcelIds = payload.passedParcelsIds
        return newState
    }),
    on(BarterActions.setReceivedParcels, (state, payload): IBarterState => {
        const newState = cloneDeep(state)
        newState.receivedParcels = payload.receivedParcels
        newState.receivedParcelIds = payload.receivedParcelsIds
        return newState
    }),
    on(BarterActions.setBarterPassedParcelsAndAcreages, (state, payload): IBarterState => {
        const newState = cloneDeep(state)
        newState.passedParcels = payload.parcels
        newState.passedParcelIds = payload.parcelIds
        newState.passedParcelAcreages = payload.acreages
        newState.passedParcelAcreagesSum = payload.sum
        newState.passedParcelGeometries = payload.passedParcelGeometries
        return newState
    }),
    on(BarterActions.setBarterPassedGeometries, (state, { geometries }): IBarterState => {
        const newState = cloneDeep(state)
        newState.passedParcelGeometries = geometries
        return newState
    }),
    on(BarterActions.setBarterReceivedParcelsAndAcreages, (state, payload): IBarterState => {
        const newState = cloneDeep(state)
        newState.receivedParcels = payload.parcels
        newState.receivedParcelIds = payload.parcelIds
        newState.receivedParcelAcreages = payload.acreages
        newState.receivedParcelAcreagesSum = payload.sum
        newState.receivedParcelGeometries = payload.receivedParcelGeometries || newState.receivedParcelGeometries
        return newState
    }),
    on(BarterActions.setBarterReceivedGeometries, (state, { geometries }): IBarterState => {
        const newState = cloneDeep(state)
        newState.receivedParcelGeometries = geometries
        return newState
    }),
    on(BarterActions.setBarterNote, (state, {note}): IBarterState => {
        const newState = cloneDeep(state)
        newState.barter.note = note
        return newState
    }),
    on(BarterActions.setBarterEfficiencyFromDate, (state, {date}): IBarterState => {
        const newState = cloneDeep(state)
        newState.barter.efficiencyFromDate = date
        return newState
    }),
    on(BarterActions.setBarterDurationMonths, (state, {durationMonths}): IBarterState => {
        const newState = cloneDeep(state)
        newState.barter.durationMonths = durationMonths
        return newState
    }),
    on(BarterActions.setBarterDurationMonthsUnit, (state, {durationMonthsUnit}): IBarterState => {
        const newState = cloneDeep(state)
        newState.barter.durationMonthsUnit = durationMonthsUnit
        return newState
    }),
    on(BarterActions.setBarterDurationToDate, (state, {durationToDate}): IBarterState => {
        const newState = cloneDeep(state)
        newState.barter.durationToDate = durationToDate
        return newState
    }),
    on(BarterActions.setBarterDurationPeriod, (state, {value}): IBarterState => {
        const newState = cloneDeep(state)
        switch (value) {
            case 'notset':
                newState.barter.reusedPreviousExpiration = false
                newState.barter.indefinite = true
                newState.barter.durationMonths = 0
                newState.barter.durationToDate = null
                break
            case 'interval':
                newState.barter.reusedPreviousExpiration = false
                newState.barter.indefinite = false
                newState.barter.durationToDate = null
                break
            case 'todate':
                newState.barter.reusedPreviousExpiration = false
                newState.barter.indefinite = false
                newState.barter.durationMonths = 0
                break
            case 'previous':
                newState.barter.reusedPreviousExpiration = true
        }
        return newState
    }),
    on(BarterActions.reset, (state): IBarterState => {
        const newState = cloneDeep(INIT_STATE)
        // Preserve parcelOrder across resets to match original behavior where sort state
        // persists between navigations (tests depend on sort order from previous scenarios)
        newState.parcelOrder = cloneDeep(state.parcelOrder)
        return newState
    }),
    on(BarterActions.resetParcelOrder, (state): IBarterState => {
        const newState = cloneDeep(state)
        newState.parcelOrder = cloneDeep(INIT_STATE.parcelOrder)
        return newState
    }),
    // Called from ca-end-dialog
    on(BarterActions.setBarterEnd, (state, { dates }): IBarterState => (
        { ...state, barter: { ...state.barter, ...dates } }
    )),
    // Feature Barter CREATE
    on(BarterActions.emptyBarterParcels, (state): IBarterState => {
        const newState = cloneDeep(state)
        const keysToEmpty = [
            'concernedParcels',
            'concernedParcelIds',
            'concernedParcelAcreages',
            'counterpartyParcels',
            'counterpartyParcelIds',
            'counterpartyParcelAcreages',
            'passedParcels',
            'passedParcelIds',
            'passedParcelAcreages',
            'passedParcelGeometries',
            'receivedParcels',
            'receivedParcelIds',
            'receivedParcelAcreages',
            'receivedParcelGeometries'
        ]
        keysToEmpty.forEach(key => {
            newState[key] = []
        })
        return newState
    }),

)

export interface IAppBarter extends Barter {
    concernedId: number;
    counterpartyId: number;
}

export interface IAppBarterResponse extends BarterResponseDto {
    concernedId: number;
    counterpartyId: number;
}


export interface IBarterState {
    barter: IAppBarter | IAppBarterResponse;
    concernedParcelIds: number[];
    concernedParcels: ParcelBarterEntity[];
    counterpartyParcels: ParcelBarterEntity[];
    counterpartyParcelIds: number[];
    concernedParcelAcreages: any[];
    concernedParcelAcreagesSum: number;
    counterpartyParcelAcreages: any[];
    counterpartyParcelAcreagesSum: number;
    concernedParcelGroupsOwnerships: any[]; // map of key 'katuzeKod-lv' -> ownership object
    counterpartyParcelGroupsOwnerships: any[];// map of key 'katuzeKod-lv' -> ownership object
    passedReceivedParcelsMapMode: null | 'pass' | 'receive'; // null || 'pass' || 'receive'
    passedParcelIds: number[];
    passedParcels: any[];
    passedParcelAcreages: any[];
    passedParcelAcreagesSum: number;
    passedParcelGeometries: any[];
    passedParcelGroupsOwnerships: any[]; // map of key 'katuzeKod-lv' -> ownership object
    receivedParcelIds: number[];
    receivedParcels: any[];
    receivedParcelAcreages: any[];
    receivedParcelAcreagesSum: number;
    receivedParcelGeometries: any[];
    receivedParcelGroupsOwnerships: any[]; // map of key 'katuzeKod-lv' -> ownership object
    parcelConflicts: any[];
    barterResult: Object;
    isEditingBarter: boolean;
    isAnyConcernedOrCounterpartyParcelUpdated: boolean;
    parcelOrder: { sortedBy: string; desc: boolean }[];
    incomingPayments: any[];
    outgoingPayments: any[];
    selectedTab: BarterTabEnum;
    reloadMapParcels: boolean;
}

export interface IBarterEnd {
    endDate: string;
    endType: string;
    resignDate: string;
    expirationDate: string;
}